import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import type { Befund, Probe, Rasterfeld } from '../types';
import { EBV_KLASSEN } from '../kataloge/ebvKlassen';
import { GEFAHRSTOFFE } from '../kataloge/gefahrstoffe';
import { einstufungAusBefunden, klassenLabel } from '../einstufung';
import {
    AuswahlFeld, HakenFeld, Leerzeile, NeuZeile, TextFeld, Untertitel, ZahlFeld, ZeilenKarte,
} from './bausteine';

/**
 * Erfassung der Rasterfelder, ihrer Proben und der Messwerte.
 *
 * ═══ DIE ORDNUNG IST DIE FACHLICHKEIT ═════════════════════════════════════
 * Feld → Probe je SCHICHT → Befunde. Die mittlere Ebene ist der Grund fuer
 * diesen Aufbau: eine Probe gilt fuer genau einen Horizont, und aus der
 * Tiefenlage folgt spaeter die Schutzklasse. Wer Messwerte direkt am Feld
 * erfasst, verliert die Unterscheidung zwischen „oben belastet" und
 * „durchgaengig belastet" — und damit die Begruendung der hoechsten Klasse.
 *
 * ═══ WARUM DIE EINSTUFUNG NICHT AUTOMATISCH GESETZT WIRD ══════════════════
 * Der Vorschlag steht daneben und wird auf Klick uebernommen. Die
 * EBV-Einstufung kommt aus dem abfallrechtlichen Gutachten; dieses Modul
 * kennt nur wenige belastbare Schwellen und darf den Rest nicht raten.
 */

interface Props {
    felder: Rasterfeld[];
    proben: Probe[];
    gesperrt: boolean;
    feldAnlegen: (feld_nr: string) => Promise<unknown>;
    feldAendern: (id: string, u: Partial<Rasterfeld>) => void;
    feldLoeschen: (id: string) => void;
    probeAnlegen: (rasterfeld_id: string, mischprobe: string) => Promise<unknown>;
    probeAendern: (id: string, u: Partial<Probe>) => void;
    probeLoeschen: (id: string) => void;
}

const KLASSEN_OPTIONEN = EBV_KLASSEN.map(k => ({ wert: k.key, label: k.label }));
const STOFF_OPTIONEN = GEFAHRSTOFFE.map(s => ({ wert: s.key, label: s.name }));

export const BefundeEditor: React.FC<Props> = ({
    felder, proben, gesperrt,
    feldAnlegen, feldAendern, feldLoeschen,
    probeAnlegen, probeAendern, probeLoeschen,
}) => {
    const [offen, setOffen] = useState<Set<string>>(new Set());
    const umschalten = (id: string) => setOffen(s => {
        const n = new Set(s);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    return (
        <>
            <NeuZeile
                platzhalter="Feldnummer, z. B. 51"
                knopf="Feld anlegen"
                gesperrt={gesperrt}
                breite="w-48"
                onAnlegen={feldAnlegen}
            />

            {felder.length === 0 ? (
                <Leerzeile text="Noch kein Rasterfeld erfasst." />
            ) : (
                <ul className="space-y-3">
                    {felder.map(f => {
                        const eigene = proben.filter(p => p.rasterfeld_id === f.id);
                        const auf = offen.has(f.id);
                        return (
                            <ZeilenKarte
                                key={f.id}
                                gesperrt={gesperrt}
                                onLoeschen={() => feldLoeschen(f.id)}
                                loeschTitel={`Feld ${f.feld_nr} löschen`}
                                kopf={
                                    <button
                                        type="button"
                                        onClick={() => umschalten(f.id)}
                                        className="flex items-center gap-2 text-left w-full"
                                    >
                                        {auf ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                            : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                        <span className="font-bold text-slate-800">Feld {f.feld_nr}</span>
                                        <span className="text-xs text-slate-500">
                                            {f.bezeichnung || 'ohne Bezeichnung'} · {eigene.length} Probe(n)
                                        </span>
                                    </button>
                                }
                            >
                                {auf && (
                                    <>
                                        <div className="grid sm:grid-cols-4 gap-2 mb-3">
                                            <TextFeld
                                                label="Bezeichnung"
                                                wert={f.bezeichnung}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => feldAendern(f.id, { bezeichnung: v })}
                                            />
                                            <ZahlFeld
                                                label="Fläche"
                                                wert={f.flaeche_qm}
                                                einheit="m²"
                                                gesperrt={gesperrt}
                                                onSpeichern={v => feldAendern(f.id, { flaeche_qm: v })}
                                            />
                                            <ZahlFeld
                                                label="Raster: Spalte"
                                                wert={f.raster_spalte}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => feldAendern(f.id, { raster_spalte: v })}
                                            />
                                            <ZahlFeld
                                                label="Raster: Zeile"
                                                wert={f.raster_zeile}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => feldAendern(f.id, { raster_zeile: v })}
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-500 -mt-2 mb-3">
                                            Spalte und Zeile verorten das Feld im gezeichneten Rasterplan.
                                            Ohne sie erscheint es in der Tabelle, aber nicht in der Skizze —
                                            und die Skizze sagt das dann ausdrücklich.
                                        </p>

                                        <Untertitel
                                            text="Proben"
                                            hinweis="Eine Probe gilt für genau eine Schicht. Getrennte Horizonte werden getrennt erfasst."
                                        />

                                        <NeuZeile
                                            platzhalter="Mischprobe, z. B. 51-1"
                                            knopf="Probe"
                                            gesperrt={gesperrt}
                                            breite="w-44"
                                            onAnlegen={m => probeAnlegen(f.id, m)}
                                        />

                                        {eigene.length === 0 ? (
                                            <Leerzeile text="Für dieses Feld ist keine Probe erfasst — das ist keine Entwarnung." />
                                        ) : (
                                            <ul className="space-y-2">
                                                {eigene.map(p => (
                                                    <ProbenZeile
                                                        key={p.id}
                                                        probe={p}
                                                        gesperrt={gesperrt}
                                                        onAendern={u => probeAendern(p.id, u)}
                                                        onLoeschen={() => probeLoeschen(p.id)}
                                                    />
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </ZeilenKarte>
                        );
                    })}
                </ul>
            )}
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════

const ProbenZeile: React.FC<{
    probe: Probe;
    gesperrt: boolean;
    onAendern: (u: Partial<Probe>) => void;
    onLoeschen: () => void;
}> = ({ probe, gesperrt, onAendern, onLoeschen }) => {
    const vorschlag = einstufungAusBefunden(probe.befunde);

    const befundAendern = (i: number, u: Partial<Befund>) => {
        onAendern({ befunde: probe.befunde.map((b, j) => (j === i ? { ...b, ...u } : b)) });
    };

    return (
        <ZeilenKarte
            gesperrt={gesperrt}
            onLoeschen={onLoeschen}
            loeschTitel={`Probe ${probe.mischprobe} löschen`}
            kopf={
                <span className="text-sm font-bold text-slate-700">
                    {probe.mischprobe || 'Probe'}
                    <span className="ml-2 px-2 py-0.5 rounded bg-slate-100 text-xs font-bold">
                        {klassenLabel(probe.einstufung, probe.vorbehalt)}
                    </span>
                </span>
            }
        >
            <div className="grid sm:grid-cols-4 gap-2 mb-2">
                <TextFeld
                    label="Mischprobe"
                    wert={probe.mischprobe}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ mischprobe: v })}
                />
                <TextFeld
                    label="Bodenansprache"
                    wert={probe.zusammensetzung}
                    platzhalter="A (S)"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ zusammensetzung: v })}
                />
                <ZahlFeld
                    label="Tiefe von"
                    wert={probe.tiefe_von}
                    einheit="m"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ tiefe_von: v })}
                />
                <ZahlFeld
                    label="Tiefe bis"
                    wert={probe.tiefe_bis}
                    einheit="m"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ tiefe_bis: v })}
                />
            </div>

            <Untertitel text="Messwerte" />
            {probe.befunde.length === 0 && (
                <p className="text-xs text-slate-400 mb-1.5">Noch kein Messwert erfasst.</p>
            )}
            <ul className="space-y-1.5 mb-2">
                {probe.befunde.map((bef, i) => (
                    <li key={i} className="grid sm:grid-cols-[1.6fr_0.8fr_0.8fr_1fr_auto] gap-2 items-end">
                        <AuswahlFeld
                            label={i === 0 ? 'Stoff' : undefined}
                            wert={bef.stoff_key}
                            optionen={STOFF_OPTIONEN}
                            gesperrt={gesperrt}
                            onSpeichern={v => befundAendern(i, {
                                stoff_key: v,
                                bezeichnung: GEFAHRSTOFFE.find(s => s.key === v)?.name ?? v,
                            })}
                        />
                        <ZahlFeld
                            label={i === 0 ? 'Wert' : undefined}
                            wert={bef.wert}
                            gesperrt={gesperrt}
                            onSpeichern={v => befundAendern(i, { wert: v })}
                        />
                        <TextFeld
                            label={i === 0 ? 'Einheit' : undefined}
                            wert={bef.einheit}
                            platzhalter="mg/kg TS"
                            gesperrt={gesperrt}
                            onSpeichern={v => befundAendern(i, { einheit: v })}
                        />
                        <AuswahlFeld
                            label={i === 0 ? 'Medium' : undefined}
                            wert={bef.medium}
                            optionen={[
                                { wert: 'feststoff' as const, label: 'Feststoff' },
                                { wert: 'eluat' as const, label: 'Eluat' },
                            ]}
                            gesperrt={gesperrt}
                            onSpeichern={v => befundAendern(i, { medium: v })}
                        />
                        {!gesperrt && (
                            <button
                                type="button"
                                onClick={() => onAendern({
                                    befunde: probe.befunde.filter((_, j) => j !== i),
                                })}
                                className="px-2 py-1.5 text-xs text-slate-400 hover:text-red-600"
                            >
                                entfernen
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            {!gesperrt && (
                <button
                    type="button"
                    onClick={() => onAendern({
                        befunde: [...probe.befunde, {
                            stoff_key: '', bezeichnung: '', wert: null,
                            einheit: 'mg/kg TS', medium: 'feststoff', quelle: '',
                        }],
                    })}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-stone-700 underline mb-3"
                >
                    <Plus className="w-3 h-3" /> Messwert
                </button>
            )}

            <div className="grid sm:grid-cols-2 gap-2 items-end">
                <div>
                    <AuswahlFeld
                        label="EBV-Einstufung"
                        wert={probe.einstufung ?? ''}
                        optionen={KLASSEN_OPTIONEN}
                        gesperrt={gesperrt}
                        onSpeichern={v => onAendern({ einstufung: v })}
                    />
                    <p className="text-[10px] text-slate-500 mt-0.5">
                        {vorschlag.status === 'abgeleitet' && vorschlag.klasse
                            ? <>Aus den Messwerten folgt <b>{vorschlag.klasse}</b> ({vorschlag.hinweis})</>
                            : vorschlag.hinweis}
                        {vorschlag.klasse && vorschlag.klasse !== probe.einstufung && !gesperrt && (
                            <button
                                type="button"
                                onClick={() => onAendern({
                                    einstufung: vorschlag.klasse,
                                    massgeblich_stoff: vorschlag.massgeblich_stoff,
                                })}
                                className="ml-1 font-bold text-stone-700 underline"
                            >
                                übernehmen
                            </button>
                        )}
                    </p>
                </div>

                <div className="space-y-1.5">
                    <HakenFeld
                        label="Analytik unvollständig (Vorbehalt)"
                        wert={probe.vorbehalt}
                        gesperrt={gesperrt}
                        onSpeichern={v => onAendern({ vorbehalt: v })}
                    />
                    {probe.vorbehalt && (
                        <div className="grid grid-cols-2 gap-2">
                            <TextFeld
                                label="Grund"
                                wert={probe.vorbehalt_grund}
                                platzhalter="Analytik teilweise ausstehend"
                                gesperrt={gesperrt}
                                onSpeichern={v => onAendern({ vorbehalt_grund: v })}
                            />
                            <TextFeld
                                label="Wiedervorlage (JJJJ-MM-TT)"
                                wert={probe.wiedervorlage_am ?? ''}
                                platzhalter="2026-09-30"
                                gesperrt={gesperrt}
                                onSpeichern={v => onAendern({ wiedervorlage_am: v || null })}
                            />
                        </div>
                    )}
                </div>
            </div>
        </ZeilenKarte>
    );
};
